const CANVAS_SIZE = 110;

export type WombatDirection = 'front' | 'left' | 'right';
export type WombatAction = 'idle' | 'jab' | 'heavy';
export type WombatSpriteSet = Record<WombatDirection, Record<WombatAction, HTMLCanvasElement>>;
export interface WombatSpritesCollection {
  player: WombatSpriteSet;
  enemy: WombatSpriteSet;
}

interface Palette {
  fur: string;
  furShade: string;
  furLight: string;
  snout: string;
  snoutShade: string;
  nose: string;
  eye: string;
  eyeHighlight: string;
  gi: string;
  giShade: string;
  belt: string;
  beltLight: string;
  headband: string;
  paw: string;
  claw: string;
  trailPunch: string;
  trailKick: string;
  shadow: string;
  outline: string;
}

interface ShapeStyle {
  stroke?: string;
  lineWidth?: number;
}

const playerPalette: Palette = {
  fur: '#8d7156',
  furShade: '#5f4634',
  furLight: '#cba984',
  snout: '#dac4ac',
  snoutShade: '#9e846d',
  nose: '#3b2a20',
  eye: '#1d120d',
  eyeHighlight: '#f8f4e6',
  gi: '#fdfaf3',
  giShade: '#d9d0c2',
  belt: '#c43a36',
  beltLight: '#f57b58',
  headband: '#e7545e',
  paw: '#c58d66',
  claw: '#f3dcc6',
  trailPunch: 'rgba(255, 225, 170, 0.55)',
  trailKick: 'rgba(120, 237, 210, 0.55)',
  shadow: 'rgba(0, 0, 0, 0.25)',
  outline: '#3a2a21',
};

const enemyPalette: Palette = {
  fur: '#77604a',
  furShade: '#4d3a2b',
  furLight: '#b89672',
  snout: '#c9b198',
  snoutShade: '#8a705d',
  nose: '#2e2118',
  eye: '#120b07',
  eyeHighlight: '#f5f1df',
  gi: '#f3efe0',
  giShade: '#cfc6b5',
  belt: '#2f5ba8',
  beltLight: '#4f7fd6',
  headband: '#4168c7',
  paw: '#b47b55',
  claw: '#ecd3bc',
  trailPunch: 'rgba(190, 225, 255, 0.5)',
  trailKick: 'rgba(86, 200, 255, 0.45)',
  shadow: 'rgba(0, 0, 0, 0.22)',
  outline: '#2d2118',
};

function buildSprites(palette: Palette): WombatSpriteSet {
  const makeCanvas = (): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    return canvas;
  };

  const drawCircle = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    fill: string,
    style?: ShapeStyle,
  ): void => {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (style?.stroke) {
      ctx.save();
      ctx.lineWidth = style.lineWidth ?? 2;
      ctx.strokeStyle = style.stroke;
      ctx.stroke();
      ctx.restore();
    }
  };

  const drawEllipse = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    rx: number,
    ry: number,
    fill: string,
    style?: ShapeStyle,
  ): void => {
    ctx.beginPath();
    ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    if (style?.stroke) {
      ctx.save();
      ctx.lineWidth = style.lineWidth ?? 2;
      ctx.strokeStyle = style.stroke;
      ctx.stroke();
      ctx.restore();
    }
  };

  const drawCapsule = (
    ctx: CanvasRenderingContext2D,
    ax: number,
    ay: number,
    bx: number,
    by: number,
    radius: number,
    fill: string,
    style?: ShapeStyle,
  ): void => {
    const angle = Math.atan2(by - ay, bx - ax);
    const length = Math.hypot(bx - ax, by - ay);
    ctx.save();
    ctx.translate(ax, ay);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.moveTo(0, -radius);
    ctx.lineTo(length, -radius);
    ctx.arc(length, 0, radius, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(0, radius);
    ctx.arc(0, 0, radius, Math.PI / 2, (Math.PI * 3) / 2);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    if (style?.stroke) {
      ctx.lineWidth = style.lineWidth ?? 2;
      ctx.strokeStyle = style.stroke;
      ctx.stroke();
    }
    ctx.restore();
  };

  const addShadow = (ctx: CanvasRenderingContext2D, y: number): void => {
    drawEllipse(ctx, 0, y, 32, 8, palette.shadow);
  };

  const drawKatana = (
    ctx: CanvasRenderingContext2D,
    hand: [number, number],
    shoulder: [number, number],
    action: WombatAction,
    outline: string,
  ): void => {
    const dx = hand[0] - shoulder[0];
    const dy = hand[1] - shoulder[1];
    const angle = Math.atan2(dy, dx);
    const bladeLength = Math.hypot(dx, dy) + 20;
    ctx.save();
    ctx.translate(hand[0], hand[1]);
    ctx.rotate(angle);

    ctx.fillStyle = '#2c2c2c';
    ctx.fillRect(-8, -2.2, 8, 4.4);
    ctx.strokeStyle = outline;
    ctx.lineWidth = 1.4;
    ctx.strokeRect(-8, -2.2, 8, 4.4);

    const bladeColor = action === 'heavy' ? '#f3faff' : '#e2e7f2';
    ctx.fillStyle = '#b7c5d8';
    ctx.fillRect(0, -1.6, bladeLength, 3.2);
    ctx.fillStyle = bladeColor;
    ctx.fillRect(0, -0.8, bladeLength * 0.98, 1.6);
    ctx.strokeStyle = outline;
    ctx.lineWidth = 1;
    ctx.strokeRect(0, -1.6, bladeLength, 3.2);

    if (action !== 'idle') {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.fillRect(bladeLength * 0.68, -1.1, 6, 2.2);
    }

    ctx.restore();
  };

  const drawTorso = (ctx: CanvasRenderingContext2D): void => {
    drawEllipse(ctx, 0, -6, 24, 24, palette.gi, { stroke: palette.outline, lineWidth: 2.2 });
    drawEllipse(ctx, 0, 7, 18, 17, palette.giShade, { stroke: palette.outline, lineWidth: 1.6 });
    drawCircle(ctx, -2, 1, 12, palette.furLight, { stroke: palette.outline, lineWidth: 1.6 });
    ctx.fillStyle = palette.belt;
    ctx.fillRect(-26, 4, 52, 7);
    ctx.fillStyle = palette.beltLight;
    ctx.fillRect(-26, 4, 52, 2.5);
    ctx.save();
    ctx.strokeStyle = palette.outline;
    ctx.lineWidth = 1.4;
    ctx.strokeRect(-26, 4, 52, 7);
    ctx.restore();
    ctx.save();
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(-24, 4.5, 20, 1.8);
    ctx.restore();
  };

  const drawHead = (ctx: CanvasRenderingContext2D, facing: 1 | -1): void => {
    drawCircle(ctx, -2 * facing, -38, 14.2, palette.fur, { stroke: palette.outline, lineWidth: 2 });
    drawCircle(ctx, -10 * facing, -43, 6, palette.furShade, { stroke: palette.outline, lineWidth: 1.3 });
    drawCircle(ctx, 7 * facing, -42, 6.4, palette.furShade, { stroke: palette.outline, lineWidth: 1.3 });
    drawCircle(ctx, -15 * facing, -45, 3.4, palette.furLight, { stroke: palette.outline, lineWidth: 1 });
    ctx.fillStyle = palette.headband;
    ctx.fillRect(-18, -45, 36, 3.6);
    ctx.fillRect(facing > 0 ? 14 : -18, -45, facing > 0 ? 8 : -8, 2.4);
    ctx.save();
    ctx.strokeStyle = palette.outline;
    ctx.lineWidth = 1;
    ctx.strokeRect(-18, -45, 36, 3.6);
    ctx.restore();
    drawCircle(ctx, 1.6 * facing, -36.5, 6, palette.snout, { stroke: palette.outline, lineWidth: 1.4 });
    drawCircle(ctx, 3.1 * facing, -35, 3.4, palette.snoutShade, { stroke: palette.outline, lineWidth: 1 });
    drawCircle(ctx, 4.1 * facing, -35.5, 1.9, palette.nose, { stroke: palette.outline, lineWidth: 0.8 });
    drawCircle(ctx, -4 * facing, -37.5, 3.6, palette.eye);
    drawCircle(ctx, -3.1 * facing, -38.7, 1.3, palette.eyeHighlight);
  };

  const drawArm = (
    ctx: CanvasRenderingContext2D,
    start: [number, number],
    end: [number, number],
    dominant: boolean,
    action: WombatAction,
  ): void => {
    drawCapsule(ctx, start[0], start[1], end[0], end[1], dominant ? 4.6 : 4.2, palette.fur, {
      stroke: palette.outline,
      lineWidth: 1.4,
    });
    if (action === 'jab' && dominant) {
      ctx.save();
      ctx.strokeStyle = palette.trailPunch;
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(start[0], start[1] - 1.5);
      ctx.lineTo(end[0], end[1] - 1.5);
      ctx.stroke();
      ctx.restore();
    }
    drawCircle(ctx, end[0], end[1], 3.4, palette.paw, { stroke: palette.outline, lineWidth: 1.2 });
    drawCircle(ctx, end[0] + 1.8, end[1] - 0.6, 1.6, palette.claw, {
      stroke: palette.outline,
      lineWidth: 0.8,
    });
    drawKatana(ctx, end, start, action, palette.outline);
  };

  const drawLeg = (
    ctx: CanvasRenderingContext2D,
    hip: [number, number],
    knee: [number, number],
    paw: [number, number],
    action: WombatAction,
    kicking: boolean,
  ): void => {
    drawCapsule(ctx, hip[0], hip[1], knee[0], knee[1], 5.2, palette.furShade, {
      stroke: palette.outline,
      lineWidth: 1.5,
    });
    drawCapsule(ctx, knee[0], knee[1], paw[0], paw[1], 5, palette.fur, {
      stroke: palette.outline,
      lineWidth: 1.5,
    });
    if (kicking && action === 'heavy') {
      drawEllipse(ctx, paw[0] + 6, paw[1] - 2.6, 11, 5.6, palette.trailKick);
    }
    drawEllipse(ctx, paw[0], paw[1], 5.2, 3.4, palette.paw, {
      stroke: palette.outline,
      lineWidth: 1.1,
    });
    drawEllipse(ctx, paw[0] + 3.2, paw[1] - 0.5, 2, 1.4, palette.claw, {
      stroke: palette.outline,
      lineWidth: 0.8,
    });
  };

  const sidePose = (action: WombatAction, facing: 1 | -1): HTMLCanvasElement => {
    const canvas = makeCanvas();
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return canvas;
    }
    ctx.translate(CANVAS_SIZE / 2, CANVAS_SIZE * 0.7);
    ctx.scale(facing, 1);

    addShadow(ctx, 10);
    drawTorso(ctx);

    drawLeg(ctx, [-14, 6], [-16, 24], [-14, 36], action, false);
    if (action === 'heavy') {
      drawLeg(ctx, [14, 4], [32, -6], [50, -2], action, true);
    } else {
      drawLeg(ctx, [14, 4], [22, 22], [20, 34], action, false);
    }

    if (action === 'heavy') {
      drawArm(ctx, [-20, -38], [-14, -74], false, action);
      drawArm(ctx, [18, -40], [28, -70], true, action);
    } else if (action === 'jab') {
      drawArm(ctx, [-20, -38], [-18, -14], false, action);
      drawArm(ctx, [18, -40], [52, -28], true, action);
    } else {
      drawArm(ctx, [-20, -38], [-18, -16], false, action);
      drawArm(ctx, [18, -40], [24, -18], true, action);
    }

    drawHead(ctx, 1);
    return canvas;
  };

  const frontPose = (): HTMLCanvasElement => {
    const canvas = makeCanvas();
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return canvas;
    }
    ctx.translate(CANVAS_SIZE / 2, CANVAS_SIZE * 0.7);
    addShadow(ctx, 10);
    drawTorso(ctx);
    drawLeg(ctx, [-12, 6], [-14, 24], [-12, 34], 'idle', false);
    drawLeg(ctx, [12, 6], [14, 24], [12, 34], 'idle', false);
    drawArm(ctx, [-20, -38], [-22, -18], false, 'idle');
    drawArm(ctx, [22, -38], [24, -18], true, 'idle');
    drawHead(ctx, -1);
    drawHead(ctx, 1);
    return canvas;
  };

  const front = frontPose();
  const rightIdle = sidePose('idle', 1);
  const rightJab = sidePose('jab', 1);
  const rightHeavy = sidePose('heavy', 1);
  const leftIdle = sidePose('idle', -1);
  const leftJab = sidePose('jab', -1);
  const leftHeavy = sidePose('heavy', -1);

  return {
    front: {
      idle: front,
      jab: front,
      heavy: front,
    },
    right: {
      idle: rightIdle,
      jab: rightJab,
      heavy: rightHeavy,
    },
    left: {
      idle: leftIdle,
      jab: leftJab,
      heavy: leftHeavy,
    },
  };
}

export function generateWombatSprites(): WombatSpritesCollection {
  return {
    player: buildSprites(playerPalette),
    enemy: buildSprites(enemyPalette),
  };
}
